import { computed } from '@vue/composition-api'
import datasetsConfig from '@/data/datasets.json'
import { getDatasetModule } from '../config/datasets'

/**
 * Keys to exclude from auto-generated field options.
 * These are internal/ID filters that don't make sense as chart grouping fields.
 */
const EXCLUDED_KEYS = new Set(['time'])
const ID_PREFIX = 'id_'

/**
 * Backend-provided temporal placeholders. The backend derives them from the
 * dataset's primary timestamp column, so they work on any dataset with a
 * `timeColumn`. Available in `by` / `columns` parameters.
 */
const TEMPORAL_PLACEHOLDERS = [
  { value: '$year', label: 'Año' },
  { value: '$month', label: 'Mes (número)' },
  { value: '$month_name', label: 'Mes' },
  { value: '$month_name_short', label: 'Mes (corto)' },
  { value: '$weekday_name', label: 'Día de la semana' },
  { value: '$weekday_name_short', label: 'Día semana (corto)' },
  { value: '$day', label: 'Día del mes' },
]

/**
 * Composable that provides available "field" options for the chart grouping
 * (maps to the Streamlit `by` parameter).
 *
 * If a dataset UI profile defines `fields`, those are used.
 * Otherwise, field options are auto-generated from the dataset's filter mappings
 * in datasets.json (e.g. "tenant" → "tenants.name").
 *
 * @param {import('@vue/composition-api').Ref<string>} selectedDataset
 * @param {import('@vue/composition-api').Ref<string>} lang - current language code
 * @returns {{ fieldOptions: import('@vue/composition-api').ComputedRef<Array> }}
 */
export function useChartFields(selectedDataset, lang) {
  const fieldOptions = computed(() => {
    const dsKey = selectedDataset.value
    if (!dsKey) return []

    const module = getDatasetModule(dsKey)

    // Temporal placeholders are available whenever the dataset has a time column.
    const temporalFields = module?.timeColumn
      ? TEMPORAL_PLACEHOLDERS.map(tp => ({
        value: tp.value,
        label: tp.label,
        rawKey: tp.value,
        temporal: true,
      }))
      : []

    // Module-defined fields
    if (module && module.fields && module.fields.length > 0) {
      const moduleFields = module.fields.map(f => ({
        value: f.processForm ? f.label : f.key,
        label: resolveLabel(f.label, lang.value),
        processForm: !!f.processForm,
        rawKey: f.key,
      }))
      return [...moduleFields, ...temporalFields]
    }

    // Auto-generate from datasets.json filters
    const cfg = datasetsConfig[dsKey]
    if (!cfg || !cfg.filters) return temporalFields

    const autoFields = Object.entries(cfg.filters)
      .filter(([key]) => !EXCLUDED_KEYS.has(key) && !key.startsWith(ID_PREFIX))
      .map(([key, column]) => ({
        value: column,
        label: formatLabel(key),
      }))
    return [...autoFields, ...temporalFields]
  })

  return { fieldOptions }
}

/**
 * Resolve a localized label object to a string.
 * Falls back to English, then first available key.
 */
function resolveLabel(labelObj, lang) {
  if (typeof labelObj === 'string') return labelObj
  if (!labelObj) return ''
  return labelObj[lang] || labelObj.en || Object.values(labelObj)[0] || ''
}

/**
 * Format a filter key into a human-readable label.
 * e.g. "master_tenant" → "Master tenant"
 */
function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
}
